/**
 * Adjust-balance drawer for one employee × leave-type row.
 * Calls `payroll.adjustLeaveBalance` with a signed `delta_days_scaled`
 * (positive = credit, negative = debit) and a mandatory reason string.
 *
 * The mutation requires MANAGER_ROLES (admin/owner) server-side; that gate
 * is defence-in-depth. The drawer itself is only ever rendered when the
 * OAuth grant is write-capable.
 *
 * ── Signed days ─────────────────────────────────────────────────────────────
 *
 * The user enters an unsigned magnitude plus a +/− direction toggle. The
 * drawer multiplies by -1 for debit before calling the mutation. The wire
 * value is ×100 (e.g. 3 days = 300), matching the backend Zod schema's
 * `delta_days_scaled` field.
 */
import * as React from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Alert,
  AutoGrowTextarea,
  Button,
  Drawer,
  StyledSelect,
  type StyledSelectOption,
} from '@jumpstone/ui-react';
import type { EntityLeaveBalanceRow } from '../../data/use_entity_leave_balances';

export type LeaveAdjustResult = { outcome: 'success' } | { outcome: 'failure' };

/**
 * A balance row plus the optional human-readable fields the drawer
 * description binds to (employee display name and leave-type label).
 * Callers that already have the row's employee name / leave-type label at
 * hand set them; the description renders the id/code as a fallback so a
 * mid-fetch or missing lookup still shows something meaningful.
 */
export interface LeaveAdjustTarget extends EntityLeaveBalanceRow {
  employee_name?: string;
  leave_type_label?: string;
}

export interface LeaveAdjustDrawerProps {
  target: LeaveAdjustTarget | null;
  onOpenChange: (open: boolean) => void;
  adjust: (input: {
    employee_id: string;
    leave_type: string;
    delta_days_scaled: number;
    reason: string;
  }) => Promise<LeaveAdjustResult>;
}

const DIRECTION_OPTIONS: StyledSelectOption[] = [
  { value: 'credit', label: '+' },
  { value: 'debit', label: '−' },
];

export function LeaveAdjustDrawer({
  target,
  onOpenChange,
  adjust,
}: LeaveAdjustDrawerProps): React.JSX.Element {
  const { t } = useTranslation('wp');

  const [days_raw, set_days_raw] = React.useState('');
  const [direction, set_direction] = React.useState<'credit' | 'debit'>(
    'credit',
  );
  const [reason, set_reason] = React.useState('');
  const [error, set_error] = React.useState<string | null>(null);
  const [days_error, set_days_error] = React.useState<string | null>(null);
  const [reason_error, set_reason_error] = React.useState<string | null>(null);
  const [is_submitting, set_is_submitting] = React.useState(false);

  const open = target !== null;

  React.useEffect(() => {
    if (!open) {
      set_days_raw('');
      set_direction('credit');
      set_reason('');
      set_error(null);
      set_days_error(null);
      set_reason_error(null);
      set_is_submitting(false);
    }
  }, [open]);

  async function handle_submit(): Promise<void> {
    set_error(null);
    set_days_error(null);
    set_reason_error(null);

    const days_float = parseFloat(days_raw);
    let valid = true;

    if (isNaN(days_float) || days_float <= 0) {
      set_days_error(t('leave.adjust_days_invalid'));
      valid = false;
    }

    if (reason.trim().length < 3) {
      set_reason_error(t('leave.adjust_reason_required'));
      valid = false;
    }

    if (!valid || target === null) return;

    const delta =
      Math.round(days_float * 100) * (direction === 'debit' ? -1 : 1);

    set_is_submitting(true);
    const result = await adjust({
      employee_id: target.employee_id,
      leave_type: target.leave_type,
      delta_days_scaled: delta,
      reason: reason.trim(),
    });
    set_is_submitting(false);

    if (result.outcome === 'failure') {
      set_error(t('leave.adjust_error_generic'));
      return;
    }

    onOpenChange(false);
  }

  return (
    <Drawer
      open={open}
      onClose={() => onOpenChange(false)}
      title={t('leave.adjust_drawer_title')}
    >
      <div className="flex flex-col gap-4 p-1">
        {target !== null && (
          <p className="text-[13px] text-[color:var(--ink-muted)]">
            {t('leave.adjust_drawer_description', {
              leave_type: target.leave_type_label ?? target.leave_type,
              employee: target.employee_name ?? target.employee_id,
            })}
          </p>
        )}

        {/* Direction + magnitude */}
        <div className="flex items-start gap-3">
          <label className="flex flex-col gap-1 w-[72px] shrink-0">
            <span className="nv-label">
              {t('leave.adjust_direction_label')}
            </span>
            <StyledSelect
              value={direction}
              options={DIRECTION_OPTIONS}
              onChange={(v) => {
                set_direction(v as 'credit' | 'debit');
              }}
              ariaLabel={t('leave.adjust_direction_label')}
            />
          </label>
          <label className="flex flex-col gap-1 grow">
            <span className="nv-label">{t('leave.adjust_days_label')}</span>
            <AutoGrowTextarea
              numeric
              value={days_raw}
              size="md"
              placeholder="0"
              onChange={(e) => {
                set_days_raw(e.target.value);
                set_days_error(null);
              }}
            />
            {days_error !== null && (
              <p className="nv-help is-error">{days_error}</p>
            )}
          </label>
        </div>

        {/* Reason */}
        <label className="flex flex-col gap-1">
          <span className="nv-label">{t('leave.adjust_reason_label')}</span>
          <AutoGrowTextarea
            value={reason}
            size="md"
            placeholder={t('leave.adjust_reason_placeholder')}
            onChange={(e) => {
              set_reason(e.target.value);
              set_reason_error(null);
            }}
          />
          {reason_error !== null && (
            <p className="nv-help is-error">{reason_error}</p>
          )}
        </label>

        {error !== null && (
          <Alert variant="danger" title={t('leave.adjust_error_title')}>
            {error}
          </Alert>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={is_submitting}
            onClick={() => {
              void handle_submit();
            }}
          >
            {is_submitting
              ? t('leave.adjust_submitting')
              : t('leave.adjust_submit')}
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
